import {
  ITEM_TYPES,
  battlepassCost,
  battlepassXpFor,
  claimableTiers,
  chapterIndex,
  dailyStatus,
  difficulties,
  isQuestLocked,
  isStepUnlocked,
  items,
  missionEndsAt,
  missionStatus,
  ownedCount,
  previewUpgradeCollection,
  streakRatio,
  summonLevel,
  teamHp,
  teamPower,
  upcomingTimers,
  upgradableCount,
  type BotUser,
  betsLeft,
  isSuccessAvailable,
  rollsLeft,
  successDefs,
} from '@fakerpg/core';
import { BarChart3, Check, ChevronRight, Dices, Egg, Gift, Hourglass, ScrollText, Sparkles, Swords, Trophy, type LucideIcon } from 'lucide-react';
import { useLang, useNow, usePrefs, useSave, useSession, useStore, useT } from '../game/context.js';
import { formatClock, formatCompact, formatNumber } from '../i18n/index.js';
import { navigate } from '../router.js';
import { Badge, Bar, Btn, Ring } from '../ui/components.js';
import { Sprite } from '../assets/Sprite.js';
import { Portrait } from '../ui/Portrait.js';
import { CountUp } from '../ui/effects.js';
import { play } from '../ui/sfx.js';
import { PlayerName } from '../ui/PlayerName.js';
import { TYPE_KEYS, frontier, greetingKey, leadCharacter, navBadges, stage, useLiveEnergy } from './shared.js';
import type { RouteName } from '../router.js';
import type { GameStore } from '../game/store.js';

interface Cta {
  icon: LucideIcon;
  title: string;
  sub: string;
  tone: 'gold' | 'magic' | 'success';
  run: () => void | Promise<void>;
}

async function startFrontier(store: GameStore, save: BotUser, difficulty: number): Promise<void> {
  const f = frontier(save, difficulty);
  if (!f) return navigate('adventures');
  await store.ensureChapter(f.chapter);
  await store.dispatch({ type: 'adventureStart', chapter: f.chapter, step: f.step, difficulty });
  navigate('adventures');
}

/** The single most useful next action (docs/11-ux-ui.md §1.2). */
function useCta(): Cta {
  const save = useSave();
  const store = useStore();
  const t = useT();
  const lang = useLang();
  const now = useNow(5000);
  const energy = useLiveEnergy();
  const run = useSession().run;
  if (run) {
    return { icon: Swords, tone: 'gold', title: t('home.cta.resume', { stage: stage(run.chapter, run.step) }), sub: t('home.cta.resumeSub'), run: () => navigate('adventures') };
  }
  if (save.stats.summoned.total === 0 && save.lootbox > 0) {
    return { icon: Sparkles, tone: 'magic', title: t('home.cta.summonFirst'), sub: t('home.cta.summonFirstSub', { n: save.lootbox }), run: () => navigate('summon') };
  }
  const hasCharacters = save.summoned.characters.owned.some((o) => o.total > 0);
  if (hasCharacters && !save.summoned.team.equiped.some((s) => s.characters)) {
    return {
      icon: Check,
      tone: 'success',
      title: t('home.cta.team'),
      sub: t('home.cta.teamSub'),
      run: async () => {
        await store.dispatch({ type: 'autoEquip' });
        navigate('team');
      },
    };
  }
  const daily = dailyStatus(save, now);
  if (daily.state === 'available') {
    const day = daily.streakBroken ? 1 : (save.daily.day % 15) + 1;
    return { icon: Gift, tone: 'gold', title: t('home.cta.daily', { day }), sub: t('home.cta.dailySub'), run: () => navigate('daily') };
  }
  if (missionStatus(save, now) === 'done') {
    return {
      icon: Hourglass,
      tone: 'success',
      title: t('home.cta.mission'),
      sub: t('home.cta.missionSub'),
      run: async () => {
        const r = await store.dispatch({ type: 'missionStart', kind: save.idle.type });
        if (r && !r.error) play('coin');
      },
    };
  }
  const f = frontier(save, 0);
  const cost = difficulties[0]?.cost ?? 3;
  if (f && energy >= cost && isStepUnlocked(save, f.chapter, f.step, 0)) {
    return {
      icon: Swords,
      tone: 'gold',
      title: t('home.cta.adventure', { stage: stage(f.chapter, f.step) }),
      sub: t('home.cta.adventureSub', { chapter: chapterIndex[f.chapter]?.name ?? '', cost }),
      run: () => startFrontier(store, save, 0),
    };
  }
  if (missionStatus(save, now) === 'idle') {
    return { icon: Hourglass, tone: 'success', title: t('home.cta.missionStart'), sub: t('home.cta.missionStartSub'), run: () => navigate('missions') };
  }
  if (save.lootbox >= 1) {
    return { icon: Sparkles, tone: 'magic', title: t('home.cta.summon', { n: formatNumber(lang, save.lootbox) }), sub: t('home.cta.summonSub'), run: () => navigate('summon') };
  }
  const upgradable = previewUpgradeCollection(save).upgrades;
  if (upgradable > 0) {
    return { icon: Sparkles, tone: 'magic', title: t('home.cta.upgrade', { count: upgradable }), sub: t('home.cta.upgradeSub'), run: () => navigate('collection') };
  }
  const next = upcomingTimers(save, now)[0];
  return { icon: Hourglass, tone: 'gold', title: t('home.cta.waiting'), sub: t('home.cta.waitingSub', { time: next ? formatClock(next.at - now) : '—' }), run: () => navigate('adventures') };
}

function Hero() {
  const save = useSave();
  const t = useT();
  const lang = useLang();
  const now = useNow(60_000);
  const lead = leadCharacter(save);
  const owned = ITEM_TYPES.reduce((a, type) => a + ownedCount(save, type), 0);
  const total = ITEM_TYPES.reduce((a, type) => a + items[type].length, 0);
  const fmt = (n: number) => formatCompact(lang, n);
  return (
    <section className="hero">
      <div className="hero__art">
        <Sprite asset={{ kind: 'item', type: 'characters', id: lead ?? 1 }} size={132} masked={!lead} decorative />
        <span className="hero__glow" aria-hidden="true" />
      </div>
      <div className="hero__body">
        <p className="hero__kicker">{t(greetingKey(new Date(now).getHours()))},</p>
        <h1 className="hero__name">
          <PlayerName save={save} size={30} />
        </h1>
        {save.title.equip && <p className="hero__title">« {save.title.equip} »</p>}
        {!lead && <p className="hero__hint">{t('home.recruit')}</p>}
        <div className="hero__stats">
          <span className="stat">
            <Sprite asset={{ kind: 'ui', name: 'attack' }} size={22} decorative />
            <span className="stat__value">
              <CountUp value={teamPower(save)} format={fmt} />
            </span>
            <span className="stat__label">{t('home.power')}</span>
          </span>
          <span className="stat">
            <Sprite asset={{ kind: 'ui', name: 'hp' }} size={22} decorative />
            <span className="stat__value">
              <CountUp value={teamHp(save)} format={fmt} />
            </span>
            <span className="stat__label">{t('home.hp')}</span>
          </span>
          <span className="stat">
            <Ring value={owned} max={total} size={34} stroke={4} color="var(--magic)">
              <Sparkles size={14} aria-hidden="true" />
            </Ring>
            <span className="stat__value">
              {owned}/{total}
            </span>
            <span className="stat__label">{t('home.collection')}</span>
          </span>
        </div>
      </div>
    </section>
  );
}

function CtaCard() {
  const cta = useCta();
  const t = useT();
  const Icon = cta.icon;
  return (
    <button type="button" className={`cta cta--${cta.tone}`} onClick={() => void cta.run()}>
      <span className="cta__icon">
        <Icon size={26} strokeWidth={2.2} aria-hidden="true" />
      </span>
      <span className="cta__text">
        <span className="cta__kicker">{t('home.kicker')}</span>
        <span className="cta__title">{cta.title}</span>
        <span className="cta__sub">{cta.sub}</span>
      </span>
      <ChevronRight className="cta__chev" size={24} aria-hidden="true" />
    </button>
  );
}

function Tiles() {
  const save = useSave();
  const t = useT();
  const now = useNow();
  const energy = useLiveEnergy();
  const max = save.adventures.energy.max;
  const timers = upcomingTimers(save, now);
  const energyAt = timers.find((x) => x.id === 'energy')?.at;
  const mission = missionStatus(save, now);
  const missionEnd = missionEndsAt(save);
  const daily = dailyStatus(save, now);
  const f = frontier(save, 0);
  const chapter = f ? chapterIndex[f.chapter] : undefined;
  const summonLv = Math.max(...ITEM_TYPES.map((type) => summonLevel(save.summoned[type]).level));
  const passReady = claimableTiers(save);
  // Completed quests leave the list (bot rule, P-12): today's count comes from the period counter.
  const questsDone = save.stats.quest.daily.quests.total;
  const open = (['daily', 'weekly'] as const).reduce((a, cat) => a + save.quests[cat].filter((id) => !isQuestLocked(save, cat, id, now)).length, 0);
  const questsTotal = questsDone + open;

  return (
    <div className="tiles">
      <button type="button" className="tile tile--adventure" onClick={() => navigate('adventures')}>
        <Ring value={energy} max={max} size={58} stroke={6} color="var(--energy)">
          <strong>{energy}</strong>
        </Ring>
        <span className="tile__text">
          <span className="tile__title">{t('home.tiles.adventure')}</span>
          <span className="tile__sub">{f ? t('home.tiles.adventureNext', { stage: stage(f.chapter, f.step) }) : '—'}</span>
          <span className="tile__meta">{energyAt ? t('home.tiles.energyNext', { time: formatClock(energyAt - now) }) : t('home.tiles.energyFull')}</span>
        </span>
        {chapter && (
          <span className="tile__art" aria-hidden="true">
            <Portrait emoji={chapter.emoji} image={chapter.image} element={chapter.element} size={44} boss />
          </span>
        )}
      </button>

      <button type="button" className={`tile tile--mission ${mission === 'done' ? 'is-ready' : ''}`} onClick={() => navigate('missions')}>
        <Ring
          value={mission === 'running' && missionEnd ? now - save.idle.date : mission === 'done' ? 1 : 0}
          max={mission === 'running' && missionEnd ? missionEnd - save.idle.date : 1}
          size={58}
          stroke={6}
          color="var(--success)"
        >
          <Hourglass size={20} aria-hidden="true" />
        </Ring>
        <span className="tile__text">
          <span className="tile__title">{t('home.tiles.mission')}</span>
          <span className="tile__sub">
            {mission === 'done' ? t('home.tiles.missionReady') : mission === 'running' && missionEnd ? t('home.tiles.missionLeft', { time: formatClock(missionEnd - now) }) : t('home.tiles.missionIdle')}
          </span>
        </span>
        {mission !== 'running' && <Badge dot />}
      </button>

      <button type="button" className={`tile tile--daily ${daily.state === 'available' ? 'is-ready' : ''}`} onClick={() => navigate('daily')}>
        <span className="tile__gift" aria-hidden="true">
          <Gift size={30} strokeWidth={1.8} />
        </span>
        <span className="tile__text">
          <span className="tile__title">{t('home.tiles.daily')}</span>
          <span className="tile__sub">
            {daily.state === 'available' ? t('home.tiles.dailyReady', { day: daily.streakBroken ? 1 : (save.daily.day % 15) + 1 }) : t('home.tiles.dailyDone', { time: formatClock(daily.nextAt - now) })}
          </span>
          <span className="tile__meta">
            🔥 {t('home.tiles.streak', { n: save.stats.reward.inARow })} · ×{streakRatio(save.stats.reward.inARow)}
          </span>
        </span>
        {daily.state === 'available' && <Badge dot />}
      </button>

      <button type="button" className={`tile tile--quests ${passReady > 0 ? 'is-ready' : ''}`} onClick={() => navigate('quests', passReady > 0 ? 'pass' : undefined)}>
        <Ring value={save.battlepass.xp - battlepassXpFor(save.battlepass.level)} max={battlepassCost(save.battlepass.level + 1)} size={58} stroke={6} color="var(--gold)">
          <ScrollText size={20} aria-hidden="true" />
        </Ring>
        <span className="tile__text">
          <span className="tile__title">{t('home.tiles.quests')}</span>
          <span className="tile__sub">{t('home.tiles.questsDone', { done: questsDone, total: questsTotal })}</span>
          <span className="tile__meta">{passReady > 0 ? t('home.tiles.passReady', { n: passReady }) : t('home.tiles.passTier', { tier: save.battlepass.level + 1 })}</span>
        </span>
        {passReady > 0 && <Badge count={passReady} tone="gold" />}
      </button>

      <button type="button" className="tile tile--summon" onClick={() => navigate('summon')}>
        <span className="tile__portal" aria-hidden="true">
          <span />
        </span>
        <span className="tile__text">
          <span className="tile__title">{t('home.tiles.summon')}</span>
          <span className="tile__sub">{t('home.tiles.summonCount', { n: save.lootbox })}</span>
          <span className="tile__meta">{t('summon.rarityLevel', { level: summonLv })}</span>
        </span>
      </button>
    </div>
  );
}

function Quest() {
  const save = useSave();
  const store = useStore();
  const prefs = usePrefs();
  const t = useT();
  if (prefs.tour.done) return null;
  const steps = [
    { done: save.stats.summoned.total > 0, label: t('home.quest.summon'), route: 'summon' as const },
    { done: save.summoned.team.equiped.some((s) => s.characters > 0), label: t('home.quest.equip'), route: 'team' as const },
    { done: save.adventures.normal.step > 0 || save.adventures.normal.chapter > 0, label: t('home.quest.adventure'), route: 'adventures' as const },
  ];
  const done = steps.filter((s) => s.done).length;
  return (
    <section className="quest">
      <header className="quest__head">
        <h2>{t('home.quest.title')}</h2>
        <span className="quest__count">{t('home.quest.progress', { done, total: steps.length })}</span>
      </header>
      <Bar value={done} max={steps.length} tone="gold" size="sm" label={t('home.quest.title')} />
      {done === steps.length ? (
        <p className="quest__done">{t('home.quest.done')}</p>
      ) : (
        <ol className="quest__steps">
          {steps.map((s) => (
            <li key={s.label}>
              <button type="button" className={`quest__step ${s.done ? 'is-done' : ''}`} onClick={() => navigate(s.route)} disabled={s.done}>
                <span className="quest__check" aria-hidden="true">
                  {s.done && <Check size={14} strokeWidth={3} />}
                </span>
                {s.label}
              </button>
            </li>
          ))}
        </ol>
      )}
      <Btn variant="ghost" size="sm" onClick={() => store.setPrefs({ tour: { step: 3, done: true } })}>
        {t('home.quest.dismiss')}
      </Btn>
    </section>
  );
}

function CollectionSummary() {
  const save = useSave();
  const t = useT();
  return (
    <section className="colsum">
      <h2 className="section-title">
        <Sparkles size={18} aria-hidden="true" /> {t('home.collection')}
      </h2>
      <div className="colsum__grid">
        {ITEM_TYPES.map((type) => {
          const owned = ownedCount(save, type);
          const total = items[type].length;
          const ready = upgradableCount(save, type);
          return (
            <button key={type} type="button" className="colsum__item" onClick={() => navigate('collection', type)}>
              <Ring value={owned} max={total} size={52} stroke={5} color="var(--magic)">
                <small>{owned}</small>
              </Ring>
              <span className="colsum__text">
                <strong>{t(TYPE_KEYS[type])}</strong>
                <span className="muted small">
                  {owned}/{total}
                  {ready > 0 && <span className="colsum__ready"> · ↑ {ready}</span>}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

/** Screens without a tile, so phones (whose tab bar shows only the main five) can reach them. */
function MoreActivities() {
  const save = useSave();
  const t = useT();
  const session = useSession();
  const now = useNow(5000);
  const badges = navBadges(save, session, now);
  const available = successDefs.filter(isSuccessAvailable);
  const unlocked = available.filter((d) => save.success.unlocked.includes(d.id)).length;
  const items: { route: RouteName; icon: LucideIcon; title: string; sub: string }[] = [
    { route: 'attributes', icon: Egg, title: t('nav.attributes'), sub: rollsLeft(save, now) > 0 ? t('home.more.roll') : t('home.more.rollWait') },
    { route: 'games', icon: Dices, title: t('nav.games'), sub: betsLeft(save, now) > 0 ? t('home.more.bet') : t('home.more.games') },
    { route: 'success', icon: Trophy, title: t('nav.success'), sub: t('home.more.success', { n: unlocked, max: available.length }) },
    { route: 'stats', icon: BarChart3, title: t('nav.stats'), sub: t('home.more.stats') },
  ];
  return (
    <section className="more">
      <h2 className="section-title">{t('home.more.title')}</h2>
      <div className="more__grid">
        {items.map((i) => {
          const Icon = i.icon;
          return (
            <button key={i.route} type="button" className="more__item" onClick={() => navigate(i.route)}>
              <span className="more__icon">
                <Icon size={20} aria-hidden="true" />
                {badges[i.route]?.dot && <Badge dot />}
              </span>
              <span className="more__text">
                <span className="more__title">{i.title}</span>
                <span className="more__sub">{i.sub}</span>
              </span>
              <ChevronRight size={16} className="more__chev" aria-hidden="true" />
            </button>
          );
        })}
      </div>
    </section>
  );
}

export function Home() {
  return (
    <div className="page page--home">
      <div className="home__main">
        <Hero />
        <CtaCard />
        <Quest />
      </div>
      <div className="home__side">
        <Tiles />
        <MoreActivities />
        <CollectionSummary />
      </div>
    </div>
  );
}
